import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  BehaviorSubject,
  firstValueFrom,
  Observable,
  Subject,
  throwError
} from 'rxjs';
import { timeout } from 'rxjs/operators';
import { ApiUrl } from '../app/apiurl.models';
import { ApiService } from '../app/services/api.service';
import { User } from '../app/pages/apps/chat/chats.model';
import { AuthSessionService } from '../app/core/auth/auth-session.service';
import { TokenStore } from '../app/core/auth/token-store';


@Injectable({
  providedIn: 'root'
})
export class UsersService {
  private subject = new Subject<any>();
  // Getter, not a plain field: a plain field would capture getJWTToken()'s
  // return value once, at UsersService construction time (before login
  // ever happens), and never re-evaluate it again - every call through
  // this.options afterwards would carry that first, stale value forever,
  // log in or out as many times as you like. Only getUserDetails() uses
  // this today, but a getter costs nothing and removes the trap.
  private get options() {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: this.apiUrl.getJWTToken()
      })
    };
  }

  public url = 'api/users';

  constructor(
    public http: HttpClient,
    public apiUrl: ApiUrl,
    private apiService: ApiService,
    private authSession: AuthSessionService
  ) {}

  private handleObserverError(error: any): Observable<any> {
    console.log(error);
    return throwError(error.error || error);
  }

  async auth(registerForm: any) {
    try {
      // A login never carries the previous session's token (2026-10-24).
      const response = await firstValueFrom(
        this.http.post<any>(
          this.apiUrl.loginUser(),
          JSON.stringify(registerForm.value),
          { headers: new HttpHeaders({ 'Content-Type': 'application/json' }), withCredentials: true }
        )
      );

      // JWT-only: the legacy DRF token from this response is no longer
      // stored at all - login is JWT-only now, kept only in TokenStore
      // (access, in memory) and the HttpOnly sale4all_refresh cookie
      // (refresh, never touched by JS).
      // Token -> JWT migration: guarded, additive. Ignored entirely if this
      // response carries no JWT pair (an older backend) - the legacy token
      // above keeps working either way. The refresh token is never read
      // here: it already arrived as an HttpOnly `sale4all_refresh` cookie
      // this JS cannot see, which is the point - see auth-session.service.ts.
      const jwtAccess = response?.['user']?.['access'];
      if (jwtAccess) {
        TokenStore.set(jwtAccess);
      }
      // Guarded writes: the backend's login response (accounts/views.py
      // UserResponse()) does not actually include is_superuser, address or
      // role, and its phone field is 'phoneNumber', not 'phone_number' - a
      // plain response['user']['x'] on any of those was writing the literal
      // string "undefined" into localStorage (JS stringifies undefined on
      // setItem), which every later reader then treated as real data - e.g.
      // isSuperUser()'s localStorage.getItem('is_superuser') === 'true'
      // check always failed silently. Only write a key the response
      // actually provided.
      const u = response['user'] || {};
      if (u.is_superuser !== undefined) localStorage.setItem('is_superuser', u.is_superuser);
      if (u.first_name !== undefined) localStorage.setItem('first_name', u.first_name);
      if (u.last_name !== undefined) localStorage.setItem('last_name', u.last_name);
      if (u.email !== undefined) localStorage.setItem('email', u.email);
      if (u.avatar !== undefined) localStorage.setItem('avatar', u.avatar);
      if (u.phoneNumber !== undefined) localStorage.setItem('phone_number', u.phoneNumber);
      if (u.address !== undefined) localStorage.setItem('address', u.address);
      if (u.role !== undefined) localStorage.setItem('role', u.role);
      // SEC-06 (SCRUM-351): with admin 2FA enforced the server treats this
      // token as a regular user until a code is entered, so user_info would
      // say "not admin". Hand over to the 2FA step instead of signing out.
      if (response?.status == 200 && jwtAccess && u.mfa_required) {
        return { status: 'MFA', enrolled: !!u.mfa_enrolled };
      }
      // 2026-10-24: the admin panel is for superusers only. The login
      // response does not say who is an admin, so ask user_info and sign a
      // regular user straight out again (the server's IsSuperUser /
      // request_is_admin checks stay the real gate).
      if (response?.status == 200 && jwtAccess) {
        const admin = await this.verifyAdmin(true);
        if (admin !== true) {
          this.signOut();
          return { status: 403, error: 'ADMIN_ONLY' };
        }
      }
      return response
    } catch (e) {
      return e
    }
  }

  // ── SEC-06 (SCRUM-351): admin 2FA ──────────────────────────────────
  // The interceptor never adds Authorization in this app - every call must
  // send this.options (Bearer token) or the server answers 401 (bug found on dev 2026-11-03).
  /** Start enrolment -> { secret, otpauth_uri, qr } (qr = PNG data URI). */
  mfaSetup(): Promise<any> {
    return firstValueFrom(this.http.post<any>(this.apiUrl.adminMfa('setup'), {}, this.options));
  }

  /** First code from the app -> { backup_codes } and a 2FA session. */
  async mfaConfirm(code: string): Promise<any> {
    const res = await firstValueFrom(
      this.http.post<any>(this.apiUrl.adminMfa('confirm'), { code }, this.options));
    return this.finishMfa(res);
  }

  /** Code from the app (or a backup code) -> a 2FA session. */
  async mfaVerify(code: string): Promise<any> {
    const res = await firstValueFrom(
      this.http.post<any>(this.apiUrl.adminMfa('verify'), { code }, this.options));
    return this.finishMfa(res);
  }

  /** The server swapped the session: keep the new access token, then run the
   *  normal admin check (user_info now answers is_superuser=true). */
  private async finishMfa(res: any): Promise<any> {
    const access = res?.user?.access;
    if (access) TokenStore.set(access);
    const admin = await this.verifyAdmin(true);
    if (admin !== true) {
      this.signOut();
      return { status: 403, error: 'ADMIN_ONLY' };
    }
    return { status: 200, backup_codes: res?.backup_codes || [] };
  }

  /** Cached result of verifyAdmin() for this page load (null = not asked yet). */
  private adminVerified: boolean | null = null;

  /**
   * True when the signed-in user is a superuser (GET user/user_info/).
   * Never hangs: times out after 10 s. On a network / server error it falls
   * back to the flag stored at the last successful check, so an admin is not
   * thrown out by a flaky connection; a 401 / 403 / not-superuser is false.
   */
  async verifyAdmin(force = false): Promise<boolean> {
    if (!force && this.adminVerified !== null) return this.adminVerified;
    if (!TokenStore.isLoggedIn()) return false;
    try {
      const info: any = await firstValueFrom(
        this.http.get(this.apiUrl.getUserInfo(), this.options).pipe(timeout(10000))
      );
      this.adminVerified = info?.is_superuser === true;
      localStorage.setItem('is_superuser', String(this.adminVerified));
      return this.adminVerified;
    } catch (e: any) {
      if (e?.status === 401 || e?.status === 403) {
        this.adminVerified = false;
        return false;
      }
      return localStorage.getItem('is_superuser') === 'true';
    }
  }

  signOut() {
    this.adminVerified = null;
    // Token -> JWT migration: revoke the refresh token server-side and clear
    // the in-memory JWT access token. Fire-and-forget - signOut() is
    // synchronous everywhere it's called - but this is the one place a
    // stolen admin refresh token actually gets revoked instead of staying
    // valid for its full 60-day lifetime.
    this.authSession.logout();

    // Cleanup only - nothing writes 'token' anymore, but this clears out
    // any leftover value a browser stored before this migration.
    localStorage.removeItem('token');
    localStorage.removeItem('is_superuser');
    localStorage.removeItem('first_name');
    localStorage.removeItem('last_name');
    localStorage.removeItem('role');
    localStorage.removeItem('phone_number');
    localStorage.removeItem('address');
    localStorage.removeItem('email');
    localStorage.removeItem('avatar');
    localStorage.removeItem('userDetails');
  }

  getFirstName(): string | null {
    return localStorage.getItem('first_name');
  }

  getLastName(): string | null {
    return localStorage.getItem('last_name');
  }
  getAddress(): string | null {
    return localStorage.getItem('address');
  }

  getEmail(): string | null {
    return localStorage.getItem('email');
  }

  getUserDetails() {
    return this.http.get(this.apiUrl.getUserDetails(), this.options);
  }

  getAvatar(): string | null {
    return localStorage.getItem('avatar');
  }

  getPhoneNumber(): string | null {
    return localStorage.getItem('phone_number');
  }

  getFullName(): string {
    const first = this.getFirstName() || '';
    const last = this.getLastName() || '';
    return `${first} ${last}`.trim();
  }

  getToken(): string | null {
    return TokenStore.get();
  }

  getRole(): string | null {
    return localStorage.getItem('role');
  }

  isSuperUser(): boolean {
    return localStorage.getItem('is_superuser') === 'true';
  }

  getUser(): any {
    return {
      token: this.getToken(),
      is_superuser: this.isSuperUser(),
      first_name: this.getFirstName(),
      last_name: this.getLastName(),
      email: this.getEmail(),
      avatar: this.getAvatar(),
      phoneNumber: this.getPhoneNumber(),
      fullName: this.getFullName()
    };
  }
}
